import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash, X, PencilSimple, Confetti, Heart, ForkKnife } from '@phosphor-icons/react';
import { EpLabel } from './Shared';

const RELATIONSHIPS = ['uncle', 'aunty', 'friend', 'colleague', 'neighbour', 'cousin', 'sir', 'in-laws', 'boss', 'other'];

export default function RememberedGuests({ api }) {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/meals/remembered-guests');
      setGuests(r.data || []);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleDelete = async (id) => {
    if (!window.confirm('Forget this guest?')) return;
    try {
      await api.delete(`/meals/remembered-guests/${id}`);
      setGuests(prev => prev.filter(g => g.guest_id !== id));
    } catch (_) { /* ignore */ }
  };

  return (
    <div className="space-y-3" data-testid="remembered-guests-section">
      <div className="glass-card p-4 flex items-center justify-between flex-wrap gap-2"
        style={{ background: 'linear-gradient(135deg, rgba(236,72,153,0.10), rgba(168,85,247,0.05))', border: '1px solid rgba(236,72,153,0.25)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <Confetti size={18} weight="fill" className="text-pink-300 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-bold text-white">Remembered Guests</h3>
            <p className="text-[11px] text-neutral-400">Recurring guests with their preferences saved. Next time you generate an occasion menu, pick them in one tap.</p>
          </div>
        </div>
        <button onClick={() => setEditing('new')} data-testid="add-guest-btn"
          className="px-3 py-1.5 rounded-lg bg-pink-500/15 text-pink-200 border border-pink-500/40 text-[11px] font-bold hover:bg-pink-500/25 inline-flex items-center gap-1 flex-shrink-0">
          <Plus size={11} weight="bold" /> Add guest
        </button>
      </div>

      {loading && (
        <div className="text-center py-6 text-neutral-500 text-sm">Loading guests…</div>
      )}

      {!loading && guests.length === 0 && (
        <div className="glass-card p-8 text-center" data-testid="remembered-guests-empty">
          <Confetti size={28} weight="duotone" className="text-pink-400/60 mx-auto mb-2" />
          <p className="text-sm text-neutral-400">No remembered guests yet.</p>
          <p className="text-[11px] text-neutral-500 mt-1">Generate a Guest Menu and hit <span className="text-pink-300 font-semibold">"Remember these guests"</span> to save them.</p>
        </div>
      )}

      {!loading && guests.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {guests.map(g => (
            <RememberedGuestCard key={g.guest_id} guest={g}
              onEdit={() => setEditing(g)} onDelete={() => handleDelete(g.guest_id)} />
          ))}
        </div>
      )}

      {editing && (
        <RememberedGuestEditModal api={api} guest={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await fetch(); }} />
      )}
    </div>
  );
}

function RememberedGuestCard({ guest, onEdit, onDelete }) {
  const visits = guest.visit_count || 0;
  return (
    <div className="glass-card p-4 relative" data-testid={`guest-card-${guest.guest_id}`}
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(236,72,153,0.20)' }}>
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h4 className="text-sm font-bold text-white truncate">{guest.name}</h4>
            {guest.relationship && <span className="text-[10px] text-pink-300 capitalize">· {guest.relationship}</span>}
          </div>
          <p className="text-[10px] text-neutral-500 mt-0.5">
            {visits > 0 ? `${visits} visit${visits === 1 ? '' : 's'}` : 'Never cooked for'}
            {guest.last_visit ? ` · last ${guest.last_visit}` : ''}
          </p>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={onEdit} data-testid={`guest-edit-${guest.guest_id}`}
            className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-400 hover:text-white" title="Edit">
            <PencilSimple size={12} />
          </button>
          <button onClick={onDelete} data-testid={`guest-delete-${guest.guest_id}`}
            className="p-1.5 rounded-lg hover:bg-red-500/10 text-neutral-400 hover:text-red-400" title="Forget">
            <Trash size={12} />
          </button>
        </div>
      </div>
      {guest.dietary_notes && (
        <p className="text-[11px] text-emerald-300/80 truncate" title={guest.dietary_notes}>
          <Heart size={9} weight="fill" className="inline mr-1" />{guest.dietary_notes}
        </p>
      )}
      {guest.restrictions && (
        <p className="text-[11px] text-amber-300/80 truncate" title={guest.restrictions}>
          ✕ {guest.restrictions}
        </p>
      )}
      {(guest.favorite_dishes || []).length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/5">
          <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1 flex items-center gap-1">
            <ForkKnife size={9} weight="fill" /> Favorites ({guest.favorite_dishes.length})
          </p>
          <div className="flex flex-wrap gap-1">
            {guest.favorite_dishes.slice(0, 4).map((d, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-pink-500/10 text-pink-200">{d}</span>
            ))}
            {guest.favorite_dishes.length > 4 && (
              <span className="text-[10px] text-neutral-500 px-1">+{guest.favorite_dishes.length - 4} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RememberedGuestEditModal({ api, guest, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: guest?.name || '',
    relationship: guest?.relationship || '',
    dietary_notes: guest?.dietary_notes || '',
    restrictions: guest?.restrictions || '',
    favorite_dishes: (guest?.favorite_dishes || []).join(', '),
    default_meal_type: guest?.default_meal_type || 'dinner',
    notes: guest?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    const payload = {
      name: form.name.trim(),
      relationship: form.relationship,
      dietary_notes: form.dietary_notes,
      restrictions: form.restrictions,
      favorite_dishes: form.favorite_dishes.split(',').map(s => s.trim()).filter(Boolean),
      default_meal_type: form.default_meal_type,
      notes: form.notes,
    };
    try {
      if (guest) await api.put(`/meals/remembered-guests/${guest.guest_id}`, payload);
      else await api.post('/meals/remembered-guests', payload);
      onSaved && onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto"
        data-testid="guest-edit-modal" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Confetti size={16} weight="fill" className="text-pink-300" />
            <h3 className="text-sm font-bold text-white">{guest ? `Edit · ${guest.name}` : 'New Remembered Guest'}</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">{error}</div>}

          <div>
            <EpLabel>Name</EpLabel>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              data-testid="guest-name" className="ep-input" placeholder="e.g., Sharma uncle, Priya didi" />
          </div>

          <div>
            <EpLabel>Relationship</EpLabel>
            <div className="flex flex-wrap gap-1.5">
              {RELATIONSHIPS.map(r => (
                <button key={r} type="button" onClick={() => setForm(f => ({ ...f, relationship: r }))}
                  data-testid={`guest-rel-${r}`}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold capitalize border ${form.relationship === r ? 'bg-pink-500/20 text-pink-200 border-pink-500/40' : 'bg-white/5 text-neutral-400 border-white/10'}`}>
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div>
            <EpLabel>Dietary notes <span className="text-[9px] text-neutral-500">what they love</span></EpLabel>
            <textarea rows="2" value={form.dietary_notes} onChange={e => setForm(f => ({ ...f, dietary_notes: e.target.value }))}
              data-testid="guest-dietary" className="ep-input" placeholder="e.g., Jain Veg, loves paneer, prefers mild" />
          </div>

          <div>
            <EpLabel>Restrictions <span className="text-[9px] text-neutral-500">what to avoid</span></EpLabel>
            <textarea rows="2" value={form.restrictions} onChange={e => setForm(f => ({ ...f, restrictions: e.target.value }))}
              data-testid="guest-restrictions" className="ep-input" placeholder="e.g., no garlic, no onion, no eggs" />
          </div>

          <div>
            <EpLabel>Favorite dishes <span className="text-[9px] text-neutral-500">comma-separated · grows automatically</span></EpLabel>
            <textarea rows="2" value={form.favorite_dishes} onChange={e => setForm(f => ({ ...f, favorite_dishes: e.target.value }))}
              data-testid="guest-favorites" className="ep-input" placeholder="Paneer Tikka, Methi Thepla, Gulab Jamun" />
          </div>

          <div>
            <EpLabel>Usual meal type</EpLabel>
            <select value={form.default_meal_type} onChange={e => setForm(f => ({ ...f, default_meal_type: e.target.value }))}
              data-testid="guest-meal-type" className="ep-input">
              <option value="breakfast" style={{ background: '#0a0a0a' }}>Breakfast</option>
              <option value="lunch" style={{ background: '#0a0a0a' }}>Lunch</option>
              <option value="tea" style={{ background: '#0a0a0a' }}>Tea / High tea</option>
              <option value="dinner" style={{ background: '#0a0a0a' }}>Dinner</option>
            </select>
          </div>

          <div>
            <EpLabel>Notes</EpLabel>
            <textarea rows="2" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              data-testid="guest-notes" className="ep-input" placeholder="e.g., Always brings sweets, eldest brother of dad" />
          </div>
        </div>

        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={handleSave} disabled={saving} data-testid="guest-save"
            className="px-4 py-2 rounded-lg bg-pink-500 text-white text-xs font-bold hover:bg-pink-400 disabled:opacity-50">
            {saving ? 'Saving…' : (guest ? 'Save changes' : 'Remember this guest')}
          </button>
        </div>

        <style>{`.ep-input{width:100%;padding:8px 12px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.10);font-size:12px;color:#fff;outline:none}.ep-input:focus{border-color:#EC4899}`}</style>
      </div>
    </div>
  );
}
